/**
 * My Safe Spaces Website - Home.js
 * =================================
 * Updated: Added Flagship Programs banner below header
 */

import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Shield,
  Users,
  Globe,
  CheckCircle,
  MessageCircle,
  Sparkles,
  AlertTriangle,
  BookOpen,
  Award,
  UserCheck,
  Clock,
  Calendar
} from 'lucide-react';
import { useContactModal } from '../context/ContactModalContext';
import { CONTACT } from '../config/constants';
import EmailButton from '../components/EmailButton';
import SEO from '../components/SEO';
import './Home.css';

const Home = () => {
  const { openContactModal } = useContactModal();
  // Flagship Programs for banner
  const flagshipPrograms = [
    { name: 'SWISS', subtitle: 'K-12 Schools', logo: '/mss/swisslogo.png', link: '/schools' },
    { name: 'SWISS HigherED', subtitle: 'Colleges & Universities', logo: '/mss/swiss-highered-logo.png', link: '/higher-education' },
    { name: 'CREW', subtitle: 'Workplace Wellness', logo: '/mss/crewlogo.png', link: '/workplace' },
    { name: 'ELEVATE', subtitle: 'Professional Development', logo: '/mss/elevatelogo.png', link: '/professional' },
    { name: 'IntelliCare AI', subtitle: 'AI Wellness', logo: '/mss/Intellicare_Logo_Green_1.png', link: '/intellicare-ai' },
    { name: 'MOSAIC', subtitle: 'Wellness Assessments', logo: '/mss/mosaic-square-logo.png', link: '/mosaic' },
    { name: 'Sessions', subtitle: 'Counseling/Therapy', logo: '/mss/MSS_Sessions_3.png', link: '/personal' },
    { name: 'MSS Labs', subtitle: 'Research & Innovation', logo: '/mss/mss-labs-logo.png', link: '/resources/labs' },
  ];

  // Upcoming Events Calendar
  const upcomingEvents = [
    { date: 'Apr 17', day: 'Fri', title: 'SWISS Webinar #1', subtitle: 'Student mental health crisis in India', type: 'webinar', link: '/schools' },
    { date: 'Apr 18', day: 'Sat', title: 'ELEVATE School Counseling', subtitle: 'Campaign Launch', type: 'launch', link: '/professional' },
    { date: 'Apr 24', day: 'Fri', title: 'Counseling Workshop #1', subtitle: 'Understanding anxiety — signs & triggers', type: 'workshop', link: '/personal' },
    { date: 'Apr 25', day: 'Sat', title: 'ELEVATE Free Webinar', subtitle: 'Mental health crisis in Indian schools', type: 'webinar', link: '/professional' },
    { date: 'May 1', day: 'Fri', title: 'SWISS Webinar #2', subtitle: 'Trauma-informed school culture', type: 'webinar', link: '/schools' },
    { date: 'May 2', day: 'Sat', title: 'ELEVATE Free Webinar', subtitle: 'CPD credits & career pathways', type: 'webinar', link: '/professional' },
    { date: 'May 9', day: 'Sat', title: 'School Counseling Kickoff', subtitle: 'ELEV-SC Program Starts', type: 'program', link: '/professional' },
    { date: 'May 16', day: 'Sat', title: 'ELEVATE Free Webinar', subtitle: 'Family systems in the Indian context', type: 'webinar', link: '/professional' },
    { date: 'May 30', day: 'Sat', title: 'Family Counseling Kickoff', subtitle: 'ELEV-FC Program Starts', type: 'program', link: '/professional' },
    { date: 'Jun 20', day: 'Sat', title: 'Couples Counseling Kickoff', subtitle: 'ELEV-CC Program Starts', type: 'program', link: '/professional' },
    { date: 'Jul 11', day: 'Sat', title: 'Workplace Wellness Kickoff', subtitle: 'ELEV-WW Program Starts', type: 'program', link: '/professional' },
    { date: 'Aug 1', day: 'Sat', title: 'Counseling Readiness Kickoff', subtitle: 'ELEV-CR Program Starts', type: 'program', link: '/professional' },
  ];

  // Four Strategic Verticals
  const verticals = [
    {
      icon: '🏫',
      title: 'Educational',
      subtitle: 'SWISS Certification',
      description: 'India\'s most comprehensive approach to school safety and student wellbeing through the SWISS certification framework.',
      features: ['Gatekeeper Training for 100% staff', 'Crisis Response Team setup', 'Student wellness programs', 'Policy framework templates'],
      link: '/schools',
      color: '#F7941D',
      logo: '/mss/swisslogo.png'
    },
    {
      icon: '🌱',
      title: 'Personal',
      subtitle: 'AI-Powered Wellness',
      description: 'Your personal mental wellness companion with AI-powered support available 24/7 in 6+ Indian languages.',
      features: ['IntelliCare AI assistant', 'Journi personal journaling', 'MOSAIC wellness assessment', 'Professional counselor access'],
      link: '/personal',
      color: '#7FB069',
      logo: '/mss/Intellicare_Logo_Green_1.png'
    },
    {
      icon: '🏢',
      title: 'Workplace',
      subtitle: 'CREW Certification',
      description: 'Transform workplace culture with evidence-based wellness programs delivering ₹4.20 ROI for every ₹1 invested.',
      features: ['Manager MHFA training', 'Employee wellness programs', 'Leadership development', 'KPI tracking & analytics'],
      link: '/workplace',
      color: '#0D5A6A',
      logo: '/mss/crewlogo.png'
    },
    {
      icon: '🌿',
      title: 'Professional',
      subtitle: 'PRISM Framework',
      description: 'Empower mental health professionals with cutting-edge tools, CPD-accredited training, and practice management.',
      features: ['150+ interactive case studies', 'CPD Standards Office accredited', 'IntelliCare-Pro AI tools', 'Supervision network access'],
      link: '/professional',
      color: '#5A8B7A',
      logo: '/mss/elevatelogo.png'
    },
  ];

  // SWISS Benefits for Institutional Safety
  const swissBenefits = [
    { icon: <Shield size={24} />, title: 'Crisis Prevention', description: 'Proactive identification and intervention before crises escalate' },
    { icon: <UserCheck size={24} />, title: '100% Staff Training', description: 'Every educator becomes a trained gatekeeper for student safety' },
    { icon: <Clock size={24} />, title: '24/7 Support Systems', description: 'Round-the-clock access to crisis resources and professional support' },
    { icon: <Award size={24} />, title: 'CPD Accredited', description: 'Internationally recognized certification from UK CPD Standards Office' },
  ];

  // The crisis stats
  const crisisStats = [
    { value: '1 in 7', label: 'Indian students face mental health challenges' },
    { value: '44%', label: 'Teachers report student distress without tools to help' },
    { value: '90%', label: 'Schools lack structured mental health protocols' },
    { value: '0.72', label: 'Effect size improvement with gatekeeper training' },
  ];

  // Research Evidence Stats
  const researchStats = [
    { value: '0.72', label: 'Knowledge', sublabel: 'Effect size (moderate-large)' },
    { value: '0.73', label: 'Confidence', sublabel: 'Effect size (moderate-large)' },
    { value: '0.69', label: 'Preparedness', sublabel: 'Effect size (moderate)' },
    { value: '₹4.20', label: 'ROI', sublabel: 'Per ₹1 invested' },
  ];

  // Partners
  const partners = [
    { name: 'India Mental Health Alliance', description: 'National Coalition', logo: '/partners/IMHA-logo.png', url: 'https://indiamentalhealthalliance.org/' },
    { name: 'Mindful Dignity Lab', description: 'Research Partner (US)', logo: '/partners/Mindful_Dignity_Lab.png', url: 'https://mindfuldignity.com/' },
    { name: 'CPD Standards Office', description: 'UK Accreditation', logo: '/partners/MySafeSpaces_CPD_Badge.png', url: 'https://directory.cpdstandards.com/providers/my-safe-spaces/' },
    { name: 'Safe Spaces Foundation', description: 'Democratizing Access', logo: '/partners/SSF-logo.png', url: 'https://www.safespacesfoundation.org/' },
    { name: 'TAISI', description: 'International Schools', logo: '/partners/taisi-logo.svg', url: 'https://www.taisiindia.com/blank' },
  ];

  return (
    <div className="home-page">
      <SEO
        title="My Safe Spaces — Mental Wellness Platform"
        description="India's first integrated mental wellness ecosystem with evidence-based programs for schools, workplaces, and individuals."
        path="/"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'My Safe Spaces',
          url: 'https://www.mysafespaces.org',
          logo: 'https://www.mysafespaces.org/mss/logo.png',
          description: "India's first integrated mental wellness ecosystem with evidence-based programs for schools, workplaces, and individuals.",
          telephone: '+918062689525',
          email: 'info@mysafespaces.org',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'San Diego',
            addressRegion: 'CA',
            addressCountry: 'US'
          },
          sameAs: [
            'https://www.linkedin.com/company/mysafespaces',
            'https://www.instagram.com/mysafespaces'
          ]
        }}
      />
      {/* ============================================
          FLAGSHIP PROGRAMS BANNER
          ============================================ */}
      <section className="flagship-banner">
        <div className="container">
          <h3 className="flagship-title">OUR FLAGSHIP PROGRAMS</h3>
          <div className="flagship-grid">
            {flagshipPrograms.map((program, index) => (
              <Link key={index} to={program.link} className="flagship-item">
                <div className="flagship-logo-wrap">
                  <img src={program.logo} alt={program.name} className="flagship-logo" />
                </div>
                <span className="flagship-name">{program.name}</span>
                <span className="flagship-subtitle">{program.subtitle}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          HERO SECTION
          ============================================ */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-grid">
            <div className="hero-content">
              <span className="hero-badge">
                <span className="badge-dot"></span>
                India's First Integrated Safety & Wellness Ecosystem
              </span>
              <h1 className="hero-title">
                Building Safer Institutions,<br />
                <span className="text-gradient">Empowering Lives</span>
              </h1>
              <p className="hero-lead">
                Comprehensive institutional safety standards and evidence-based wellness programs
                for schools, workplaces, individuals, and mental health professionals across India.
              </p>
              <div className="hero-actions">
                <button onClick={() => openContactModal()} className="btn btn-primary btn-lg">
                  Schedule Discovery Call
                  <ArrowRight size={20} />
                </button>
                <a href="#swiss-safety" className="btn btn-secondary btn-lg">
                  Why SWISS?
                </a>
              </div>
              <div className="hero-trust-badges">
                <span className="trust-badge">
                  <Award size={14} />
                  CPD Accredited
                </span>
                <span className="trust-badge">
                  <Shield size={14} />
                  Evidence-Based
                </span>
                <span className="trust-badge">
                  <Users size={14} />
                  1M+ Students Targeted
                </span>
              </div>
            </div>
            <div className="hero-events-calendar">
              <div className="events-header">
                <Calendar size={18} />
                <h3>Upcoming Events</h3>
                <span className="events-badge">Apr — Aug 2026</span>
              </div>
              <div className="events-list">
                {upcomingEvents.slice(0, 7).map((event, i) => (
                  <Link key={i} to={event.link} className={`event-row event-${event.type}`}>
                    <div className="event-date-block">
                      <span className="event-date">{event.date}</span>
                      <span className="event-day">{event.day}</span>
                    </div>
                    <div className="event-info">
                      <span className="event-title">{event.title}</span>
                      <span className="event-subtitle">{event.subtitle}</span>
                    </div>
                    <span className={`event-type-badge ${event.type}`}>{event.type}</span>
                  </Link>
                ))}
              </div>
              <Link to="/professional" className="events-view-all">
                View Full Calendar <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          SWISS INSTITUTIONAL SAFETY SECTION
          ============================================ */}
      <section id="swiss-safety" className="swiss-safety-section section">
        <div className="container">
          <div className="swiss-safety-grid">
            <div className="swiss-safety-content">
              <span className="badge badge-blue">
                <AlertTriangle size={16} />
                The Need of the Hour
              </span>
              <h2>Institutional Student Safety Cannot Wait</h2>
              <p className="swiss-lead">
                India faces a critical mental health crisis among students. Schools and colleges lack
                the protocols, trained staff, and systems needed to identify at-risk students and
                intervene effectively.
              </p>
              <p className="swiss-description">
                <strong>SWISS (Student Wellness & Institutional Safety Standards)</strong> provides
                a comprehensive framework that transforms every institution into a <em>Safe, Welcoming,
                Inclusive, and Supportive</em> environment—because every student deserves protection.
              </p>

              <div className="swiss-benefits-grid">
                {swissBenefits.map((benefit, index) => (
                  <div key={index} className="swiss-benefit-card">
                    <div className="benefit-icon">{benefit.icon}</div>
                    <div>
                      <h4>{benefit.title}</h4>
                      <p>{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Link to="/schools" className="btn btn-primary">
                Explore SWISS Certification
                <ArrowRight size={18} />
              </Link>
            </div>

            <div className="swiss-safety-stats">
              <div className="crisis-stats-card">
                <h3>The Crisis in Numbers</h3>
                {crisisStats.map((stat, index) => (
                  <div key={index} className="crisis-stat">
                    <span className="crisis-value">{stat.value}</span>
                    <span className="crisis-label">{stat.label}</span>
                  </div>
                ))}
                <div className="stats-source">
                  Source: Wang et al., 2025 Meta-Analysis
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          FOUR VERTICALS SECTION
          ============================================ */}
      <section id="verticals" className="verticals-section section">
        <div className="container">
          <div className="section-header text-center">
            <span className="badge badge-blue">
              <Globe size={16} />
              Our Solutions
            </span>
            <h2>Four Strategic Verticals</h2>
            <p>Comprehensive safety standards and wellness programs designed for every stage of life.</p>
          </div>

          <div className="verticals-grid">
            {verticals.map((vertical, index) => (
              <div key={index} className="vertical-card" style={{ '--card-accent': vertical.color }}>
                <div className="vertical-header">
                  <span className="vertical-icon-large">{vertical.icon}</span>
                  <div>
                    <h3>{vertical.title} Wellness</h3>
                    <span className="vertical-subtitle">{vertical.subtitle}</span>
                  </div>
                </div>
                <p className="vertical-description">{vertical.description}</p>
                <ul className="vertical-features">
                  {vertical.features.map((feature, fi) => (
                    <li key={fi}>
                      <CheckCircle size={16} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link to={vertical.link} className="btn btn-outline">
                  Learn More
                  <ArrowRight size={16} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          RESEARCH EVIDENCE SECTION
          ============================================ */}
      <section className="research-section section">
        <div className="container">
          <div className="section-header text-center">
            <span className="badge badge-blue">
              <BookOpen size={16} />
              Research-Backed
            </span>
            <h2>Evidence-Based Results</h2>
            <p>Our framework is built on rigorous research from meta-analysis of 16 randomized controlled trials.</p>
          </div>

          <div className="research-sources">
            <div className="source-card">
              <strong>Wang et al., 2025</strong>
              <span>Meta-analysis of 16 RCTs on gatekeeper training</span>
            </div>
            <div className="source-card">
              <strong>Mindful Dignity Lab (US)</strong>
              <span>Research partnership for cultural adaptation</span>
            </div>
            <div className="source-card">
              <strong>CPD Standards Office (UK)</strong>
              <span>International gold-standard accreditation</span>
            </div>
          </div>

          <div className="research-stats-grid">
            {researchStats.map((stat, index) => (
              <div key={index} className="research-stat">
                <span className="research-value">{stat.value}</span>
                <span className="research-label">{stat.label}</span>
                <span className="research-sublabel">{stat.sublabel}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          INTELLICARE AI SECTION
          ============================================ */}
      <section className="intellicare-section section">
        <div className="container">
          <div className="intellicare-grid">
            <div className="intellicare-content">
              <span className="badge badge-blue">
                <Sparkles size={16} />
                Powered By AI
              </span>
              <h2>Meet IntellicareAI</h2>
              <p className="intellicare-lead">
                Your compassionate AI companion for mental wellness. Available 24/7, in multiple Indian languages, with zero judgment.
              </p>
              <ul className="intellicare-features">
                <li><CheckCircle size={18} /><span>Multi-language support: English, Hindi, Tamil, Telugu & more</span></li>
                <li><CheckCircle size={18} /><span>Evidence-based coping strategies and techniques</span></li>
                <li><CheckCircle size={18} /><span>Crisis detection with human escalation</span></li>
                <li><CheckCircle size={18} /><span>Private journaling with mood tracking</span></li>
              </ul>
              <Link to="/intellicare-ai" className="btn btn-primary">
                <MessageCircle size={18} />
                Try IntellicareAI
              </Link>
            </div>
            <div className="intellicare-visual">
              <Link to="/intellicare-ai" className="intellicare-demo">
                <img src="/mss/Intellicare_Logo_Green_1.png" alt="IntellicareAI" className="intellicare-logo-large" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          PARTNERS SECTION
          ============================================ */}
      <section className="partners-section section">
        <div className="container">
          <div className="section-header text-center">
            <span className="badge badge-blue">
              <Users size={16} />
              Powered By
            </span>
            <h2>Distinguished Partner Network</h2>
          </div>
          <div className="partners-grid">
            {partners.map((partner, index) => {
              const content = (
                <>
                  {partner.logo ? (
                    <img src={partner.logo} alt={partner.name} className="partner-logo" />
                  ) : (
                    <div className="partner-text-logo">{partner.name}</div>
                  )}
                  <span className="partner-name">{partner.name}</span>
                  <span className="partner-desc">{partner.description}</span>
                </>
              );
              return partner.url ? (
                <a key={index} href={partner.url} target="_blank" rel="noopener noreferrer" className="partner-card">
                  {content}
                </a>
              ) : (
                <div key={index} className="partner-card">
                  {content}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================
          CTA SECTION
          ============================================ */}
      <section className="cta-section section">
        <div className="container">
          <div className="cta-card">
            <div className="cta-content">
              <h2>Ready to Build a Safer Institution?</h2>
              <p>Schedule a 30-minute discovery call to assess your needs and create a customized implementation roadmap.</p>
              <div className="cta-actions">
                <button onClick={() => openContactModal()} className="btn btn-white btn-lg">
                  Schedule Discovery Call
                  <ArrowRight size={20} />
                </button>
                <EmailButton />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
